import React, {useEffect, useRef, useState, useCallback} from 'react';
import ReactPlayer from 'react-player';
import {updateUserSignDocProgressAPI} from '../../Services/api';
import './VideoPlayer.scss';

/**
 * VideoPlayer with careful seeking prevention
 * @param {object} params - Component parameters
 * @param {string} params.videoUrl - The video URL to play
 * @param {number} params.startAtDecimalPercent - Starting position as decimal percentage (0-1)
 * @param {function} params.onWatchComplete - Callback when video completes
 * @param {boolean} params.preventSeeking - Whether to prevent forward seeking
 * @param {string} params.signDocId - SignDoc ID for progress tracking
 */
export const VideoPlayer = ({videoUrl, startAtDecimalPercent, onWatchComplete, preventSeeking = true, signDocId}) => {
    const playerRef = useRef(null);
    const [duration, setDuration] = useState(0);
    const [maxWatchedPosition, setMaxWatchedPosition] = useState(startAtDecimalPercent || 0);
    const [lastKnownPosition, setLastKnownPosition] = useState(startAtDecimalPercent || 0);
    const [lastSavedProgress, setLastSavedProgress] = useState(startAtDecimalPercent || 0);
    const [isResetInProgress, setIsResetInProgress] = useState(false);

    /**
     * Saves progress to backend
     * @param {object} params - Parameters object
     * @param {number} params.progressPercent - Progress as decimal percentage
     */
    const saveProgressToBackend = useCallback(async ({progressPercent}) => {
        if (!signDocId) return;

        try {
            await updateUserSignDocProgressAPI({
                signDocId,
                progressPercentage: Math.round(progressPercent * 100)
            });
        } catch (error) {
            console.error('Error saving video progress:', error);
        }
    }, [signDocId]);

    /**
     * Reset video to maximum watched position
     */
    const resetToMaxPosition = useCallback(() => {
        if (playerRef.current && !isResetInProgress) {
            setIsResetInProgress(true);
            playerRef.current.seekTo(maxWatchedPosition);

            setTimeout(() => {
                setIsResetInProgress(false);
            }, 100);
        }
    }, [maxWatchedPosition, isResetInProgress]);

    /**
     * Progress handler with seeking prevention
     * @param {object} state - Progress state from ReactPlayer
     */
    const onProgress = useCallback((state) => {
        if (isResetInProgress) return;

        const newPosition = state.played;

        // Apply seeking prevention if enabled and we have meaningful data
        if (preventSeeking && duration > 0) {
            // Allow up to 3 seconds of forward seeking
            const maxAllowedSkipSeconds = 3;
            const maxAllowedSkipDecimal = maxAllowedSkipSeconds / duration;

            // Detect illegal forward seeking: if position jumped forward MORE than 3 seconds beyond watched content
            const seekJumpFromLast = newPosition - lastKnownPosition;
            const seekJumpFromMax = newPosition - maxWatchedPosition;

            if (seekJumpFromLast > maxAllowedSkipDecimal && seekJumpFromMax > maxAllowedSkipDecimal) {
                resetToMaxPosition();
                return;
            }
        }

        // Update maximum watched position if moving forward naturally
        if (newPosition > maxWatchedPosition) {
            setMaxWatchedPosition(newPosition);
        }

        // Save progress every 15 seconds
        if ((newPosition - lastSavedProgress) * duration > 15) {
            setLastSavedProgress(newPosition);
            saveProgressToBackend({progressPercent: newPosition});
        }

        setLastKnownPosition(newPosition);
    }, [preventSeeking, duration, lastKnownPosition, maxWatchedPosition, lastSavedProgress, saveProgressToBackend, resetToMaxPosition, isResetInProgress]);

    /**
     * Handle explicit seek events (when user drags progress bar)
     * @param {number} seconds - Position in seconds
     */
    const onSeek = useCallback((seconds) => {
        if (!preventSeeking || !duration) return;

        const seekPosition = seconds / duration;

        // If seeking beyond watched content, reset after a brief delay
        if (seekPosition > maxWatchedPosition + 0.01) {
            setTimeout(() => resetToMaxPosition(), 100);
        }
    }, [preventSeeking, duration, maxWatchedPosition, resetToMaxPosition]);

    /**
     * Handle duration loaded
     * @param {number} videoDuration - Duration in seconds
     */
    const handleDuration = useCallback((videoDuration) => {
        setDuration(videoDuration);
    }, []);

    /**
     * Handle video completion
     * Only allow completion if user actually watched to near the end (95%+)
     * This prevents users from seeking to 100% to bypass the video
     */
    const handleVideoComplete = useCallback(() => {
        // If preventSeeking is enabled, ensure user actually watched most of the video
        // Use 0.95 (95%) threshold - reasonable buffer for natural playback ending
        if (preventSeeking && maxWatchedPosition < 0.95) {
            resetToMaxPosition();
            return;
        }
        // User legitimately watched to near the end - allow completion
        saveProgressToBackend({progressPercent: 1.0});
        onWatchComplete();
    }, [preventSeeking, maxWatchedPosition, resetToMaxPosition, saveProgressToBackend, onWatchComplete]);

    // Initialize starting position when duration is available
    useEffect(() => {
        if (playerRef.current && duration > 0 && startAtDecimalPercent > 0) {
            if (startAtDecimalPercent >= 1.0) {
                onWatchComplete();
            }
            playerRef.current.seekTo(startAtDecimalPercent);
            setMaxWatchedPosition(startAtDecimalPercent);
            setLastKnownPosition(startAtDecimalPercent);
        }
    }, [duration, startAtDecimalPercent]);


    return (
        <div className="VideoPlayer">
            <ReactPlayer
                width='100%'
                height='100%'
                ref={playerRef}
                url={videoUrl}
                controls={true}
                playing={true}
                onDuration={handleDuration}
                onProgress={onProgress}
                onSeek={onSeek}
                onEnded={handleVideoComplete}
                progressInterval={200}
                playsinline={true}
                stopOnUnmount={false}
                config={{
                    youtube: {
                        playerVars: {
                            disablekb: preventSeeking ? 1 : 0, // Disable keyboard controls if preventing seeking
                            loop: 0 // Disable loop for YouTube videos
                        }
                    },
                    vimeo: {
                        playerOptions: {
                            keyboard: preventSeeking ? false : true,
                            loop: false // Disable loop for Vimeo videos
                        }
                    },
                    file: {
                        attributes: {
                            controlsList: 'nodownload',
                            loop: false // Disable loop for file videos
                        }
                    }
                }}
            />

        </div>
    );
}

